import { Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import { AccountStatus } from '@/api/model'
import type { GroupNameResponse } from '@/api/model'

function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('de-DE')
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value || '—'}</span>
    </div>
  )
}

interface Props {
  fullName: string
  position?: string | null
  status?: AccountStatus
  registrationCompleted?: boolean
  employeeNumber?: string | null
  birthDate?: string | null
  employmentDate?: string | null
  subCompanyName?: string | null
  groups?: GroupNameResponse[] | null
  registrationCode?: string | null
  onboardingFileDownloadUrl?: string | null
}

export function EmployeeInfoCard({ 
  fullName,
  position,
  status,
  registrationCompleted,
  employeeNumber,
  birthDate,
  employmentDate,
  subCompanyName,
  groups,
  registrationCode,
  onboardingFileDownloadUrl

}: Props) {
  const { t } = useTranslation('employees')
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-2xl">{fullName || '—'}</CardTitle>
            {position && (<p className="text-muted-foreground mt-1">{position}</p>)}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={status === AccountStatus.Active ? 'default' : 'outline'}>
              {status === AccountStatus.Active 
                ? t('status.active', { ns: 'common' }) 
                : t('status.blocked', { ns: 'common' })}
            </Badge>
            {registrationCompleted === false && (
              <Badge variant="outline" className="text-amber-600 border-amber-400">
                {t('detail.info.pending')}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="pt-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
          <InfoRow label={t('detail.info.employeeNumber')} value={employeeNumber} />
          <InfoRow label={t('detail.info.birthDate')} value={formatDate(birthDate)} />
          <InfoRow label={t('detail.info.employmentDate')} value={formatDate(employmentDate)} />
          <InfoRow label={t('detail.info.subCompany')} value={subCompanyName} />
          <InfoRow label={t('detail.info.groups')} value={ groups?.length ? groups.map((g) => g.name).filter(Boolean).join(', ') : '—'}/>
          <InfoRow
            label={t('detail.info.registrationCode')}
            value={
              <span className="flex items-center gap-1">
                {registrationCode 
                  ? (<code className="text-xs bg-muted px-1.5 py-0.5 rounded">{registrationCode}</code>)
                  : (<span className="text-muted-foreground">—</span>)}
                {onboardingFileDownloadUrl && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={onboardingFileDownloadUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="size-4" />
                    </a>
                  </Button>
                )}
              </span>
            }
          />
        </div>
      </CardContent>
    </Card>
  )
}
